/**
 * useScrollUpPaginator — backward-infinite-scroll для Virtuoso (P2#10e).
 *
 * Управляет:
 *  - firstItemIndex state (стартует VIRT_FIRST_INITIAL=1_000_000)
 *    КРИТИЧНО для Virtuoso prepend: без этого scroll-якорь теряется после
 *    первой подгрузки → startReached перестаёт триггериться. См. hotfix 2b8cde5.
 *  - loadMoreOlder callback — fetchBefore + merge + декремент firstItemIndex
 *    + сдвиг baseline pulse-индикатора на +older.length (см. e7cfc01).
 *  - reset firstItemIndex/reachedStart при смене топика.
 *
 * Совместим с itemContent: реальный массив-индекс = idx - firstItemIndex.
 *
 * Возвращает: { firstItemIndex, loadMoreOlder } для проброса в <Virtuoso>.
 */
import { useCallback, useRef, useState } from "react";
import type { QueryClient } from "@tanstack/react-query";
import { fetchChatMessagesBefore, type ChatMessage } from "@/api/dashboard";

export const VIRT_FIRST_INITIAL = 1_000_000;

export type UseScrollUpPaginatorParams = {
  activeTopicId: number | null;
  qc: QueryClient;
  /** Ссылка на baselineMsgLenRef из ChatPage — сдвигаем на +older.length при
   *  успешной подгрузке, чтобы pulse 'new messages' не зажигался ложно. */
  baselineMsgLenRef: React.MutableRefObject<{ topicId: number; len: number } | null>;
  /** Если true — бэк не возвращает удалённые в выборку. Без этого админ
   *  получал 200 преимущественно удалённых → клиент фильтровал → лента
   *  оставалась короткой → бесконечные мелкие подгрузки и дёрганье. */
  hideDeleted?: boolean;
};

export function useScrollUpPaginator(params: UseScrollUpPaginatorParams) {
  const { activeTopicId, qc, baselineMsgLenRef, hideDeleted = false } = params;
  const loadingMoreRef = useRef<boolean>(false);
  // Реактивный флаг ТОЛЬКО для UI-индикатора подгрузки старых сообщений
  // (спиннер над лентой). Логика гейта по-прежнему на loadingMoreRef (без
  // лишних ре-рендеров). Меняется 2 раза на загрузку — редкое событие (scroll-top).
  const [loadingMore, setLoadingMore] = useState(false);
  const reachedStartRef = useRef<Set<number>>(new Set());
  // Зеркало activeTopicId — loadMoreOlder читает СВЕЖИЙ топик, а не stale-
  // замыкание. Иначе jump-из-поиска (stale jumpToMessage) грузит старый чат.
  const activeTopicRef = useRef<number | null>(activeTopicId);
  activeTopicRef.current = activeTopicId;
  // Свежий qc/hideDeleted тоже через ref — чтобы loadMoreOlder был стабилен.
  const qcRef = useRef(qc); qcRef.current = qc;
  const hideDeletedRef = useRef(hideDeleted); hideDeletedRef.current = hideDeleted;
  const baselineRef = useRef(baselineMsgLenRef); baselineRef.current = baselineMsgLenRef;
  const [firstItemIndex, setFirstItemIndex] = useState<number>(VIRT_FIRST_INITIAL);

  // Reset при смене топика — SYNCHRONOUS-DURING-RENDER pattern.
  // useEffect ниже срабатывает ПОСЛЕ mount Virtuoso → race: Virtuoso mount'ится
  // со stale firstItemIndex (от прошлого чата с снижённым через loadMoreOlder).
  // initialTopMostItemIndex = stale_firstItemIndex + len-1 → промах → лента
  // не на bottom при открытии чата (особенно заметно в DM с короткой историей).
  //
  // Решение: setState вызывается ВО ВРЕМЯ render — React detects change и
  // делает immediate re-render с правильным firstItemIndex, ДО mount Virtuoso.
  // Идемпотентно через prevTopicRef (нет бесконечного loop).
  const prevTopicRef = useRef<number | null | undefined>(activeTopicId);
  if (prevTopicRef.current !== activeTopicId) {
    prevTopicRef.current = activeTopicId;
    setFirstItemIndex(VIRT_FIRST_INITIAL);
    reachedStartRef.current.delete(activeTopicId ?? -1);
  }

  // limit: обычная прокрутка вверх — 200; прыжок к далёкому сообщению
  // (jumpToMessage → doOutOfRange) передаёт JUMP_HISTORY_PAGE.
  const loadMoreOlder = useCallback(async (limit: number = 200) => {
    // ВСЁ через ref — loadMoreOlder всегда работает с ТЕКУЩИМ топиком, даже
    // если вызван stale-замыканием (jump из поиска). Callback стабилен (deps []).
    const tid = activeTopicRef.current;
    const _qc = qcRef.current;
    if (!tid) return;
    if (loadingMoreRef.current) return;
    if (reachedStartRef.current.has(tid)) return;
    const current = _qc.getQueryData<ChatMessage[]>(["chat/messages", tid]) ?? [];
    if (current.length === 0) return;
    let oldestId = Infinity;
    for (const m of current) if (m.id > 0 && m.id < oldestId) oldestId = m.id;
    if (!Number.isFinite(oldestId)) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const older = await fetchChatMessagesBefore(tid, oldestId, limit, hideDeletedRef.current);
      if (older.length === 0) {
        reachedStartRef.current.add(tid);
        return;
      }
      _qc.setQueryData<ChatMessage[]>(["chat/messages", tid], (existing) => {
        const arr = existing ?? [];
        const byId = new Map<number, ChatMessage>();
        for (const m of older) byId.set(m.id, m);
        for (const m of arr) byId.set(m.id, m);
        return Array.from(byId.values()).sort((a, b) => a.id - b.id);
      });
      // firstItemIndex декрементим ТОЛЬКО если топик не сменился за время await
      // (иначе сорвём scroll-якорь чужого чата).
      if (activeTopicRef.current === tid) {
        setFirstItemIndex(prev => prev - older.length);
      }
      const bl = baselineRef.current.current;
      if (bl?.topicId === tid) {
        baselineRef.current.current = { topicId: tid, len: bl.len + older.length };
      }
    } catch {
      // ignore — retry на следующий scroll-top
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, []);

  // Reset firstItemIndex к VIRT_FIRST_INITIAL. Используется при pin-jump
  // (full-replace slice + remount Virtuoso) — Virtuoso стартует чисто, и
  // initialTopMostItemIndex должен использовать чистый firstItemIndex,
  // а не stale (снижённый прошлым loadMoreOlder).
  const resetFirstItemIndex = useCallback(() => {
    setFirstItemIndex(VIRT_FIRST_INITIAL);
    if (activeTopicId != null) reachedStartRef.current.delete(activeTopicId);
  }, [activeTopicId]);

  return { firstItemIndex, loadMoreOlder, resetFirstItemIndex, loadingMore };
}
